import BaseController from "./BaseController";
import JSONModel from "sap/ui/model/json/JSONModel";
import { ListBase$ItemPressEvent } from "sap/m/ListBase";
import { EnvironmentService } from "../service/Environment.service";
import { ErrorHandler } from "../service/ErrorHandler.service";
import { Route$PatternMatchedEvent } from "sap/ui/core/routing/Route";
import { Artikel, Preis, ZubehoerArtikel } from "../model/models";

/**
 * @namespace com.unitrade.crm.controller
 */
export default class ArticleDetailsController extends BaseController {
  public override async getTitle(): Promise<string> {
    return await this.getTranslatedString("ArticleDetails");
  }

  onInit(): void {
    this.setModel(new JSONModel());
    const oOwnerComponent = this.getOwnerComponent();
    const oRouter = oOwnerComponent.getRouter();

    oRouter.getRoute("articleDetails").attachPatternMatched(this._onPatternMatch, this);
  }

  private _onPatternMatch = async (oEvent: Route$PatternMatchedEvent) => {
    const layout = this.byId("article-details-view-object-page-layout") as any;
    layout._scrollTo(0, 0); // private method -> https://answers.sap.com/questions/423385/scroll-to-section-header-object-page-layout-ui5.html

    const oModel = this.getModel() as JSONModel;
    oModel.setData({});

    const args = oEvent.getParameter("arguments") as {
      idPath: string;
    };
    oModel.setProperty("/artid", Number(args.idPath));

    await this.loadArtikel();
    await this.loadPreis();
  };

  private loadArtikel = async () => {
    const oModel = this.getModel() as JSONModel;
    const artid: number = oModel.getProperty("/artid");
    try {
      oModel.setProperty("/busy", true);
      const [artikel, zubehoer, bestand] = await Promise.all([this.callArticleGet(artid), this.callZubehoerGet(artid), this.callBestandGet(artid)]);
      oModel.setProperty("/artikel", artikel);
      oModel.setProperty("/zubehoer", zubehoer);
      oModel.setProperty("/bestand", bestand);
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { onRetry: this.loadArtikel, onAbort: this.redirectToHome });
    } finally {
      oModel.setProperty("/busy", false);
    }
  };

  private loadPreis = async () => {
    const oModel = this.getModel() as JSONModel;
    const artid: number = oModel.getProperty("/artid");
    try {
      oModel.setProperty("/preisBusy", true);
      const preis = await this.callPreisGet(artid);
      oModel.setProperty("/preis", preis);
    } catch (ex: any) {
      ErrorHandler.handleError(ex, { title: "Fehler bei der Preisermittlung", onRetry: this.loadPreis, ignorable: true });
    } finally {
      oModel.setProperty("/preisBusy", false);
    }
  };

  private callArticleGet = (artid: number): JQuery.jqXHR<Artikel> => {
    return jQuery.get(`${EnvironmentService.getBaseUrl()}/artikel/${artid}`);
  };

  private callPreisGet = (artid: number): JQuery.jqXHR<Preis> => {
    return jQuery.get(`${EnvironmentService.getBaseUrl()}/artikel/${artid}/preis`);
  };

  private callZubehoerGet = (artid: number): JQuery.jqXHR<ZubehoerArtikel> => {
    return jQuery.get(`${EnvironmentService.getBaseUrl()}/artikel/${artid}/zubehoer`);
  };

  private callBestandGet = (artid: number): JQuery.jqXHR<ZubehoerArtikel> => {
    return jQuery.get(`${EnvironmentService.getBaseUrl()}/artikel/${artid}/bestand`);
  };

  private redirectToHome = () => {
    this.getOwnerComponent().getRouter().navTo("home");
  };

  public onZubehoerPress = async (oEvent: ListBase$ItemPressEvent) => {
    const oItem = oEvent.getSource();
    const oZubehoer = oItem.getBindingContext().getObject() as ZubehoerArtikel;
    this.redirectToArtikel(oZubehoer.artikelId);
  };

  private redirectToArtikel = async (artid: number) => {
    this.getOwnerComponent().getRouter().navTo("articleDetails", { idPath: artid });
  };
}
